import React, { type ChangeEvent, forwardRef, useId } from "react";
import type { BaseProps } from "@purpur/common-types";
import { Label } from "@purpur/label";
import { Paragraph } from "@purpur/paragraph";
import { Select } from "@purpur/select";
import { VisuallyHidden } from "@purpur/visually-hidden";
import c from "classnames/bind";

import styles from "./pagination-page-selector.module.scss";
import { type InternalLinkProps, type InternalNoLinkProps } from "./types";
import { usePageOptions } from "./use-page-options.hook";

const cx = c.bind(styles);

export type PaginationPageSelectorProps = Omit<BaseProps, "children"> & {
  currentPage: number;
  navigationFunction: (page: number, url: string) => void;
  numberOfPages: number;
  onPageChange: (page: number) => void;
  outOfLabel: string;
  pageSelectorId?: string;
  pageSelectorLabel: string;
} & (InternalLinkProps | InternalNoLinkProps);

const rootClassName = "purpur-pagination-page-selector";

export const PaginationPageSelector = forwardRef<HTMLDivElement, PaginationPageSelectorProps>(
  (
    {
      ["data-testid"]: dataTestId = "purpur-pagination-page-selector",
      asLink,
      className,
      currentPage,
      hrefGetter,
      navigationFunction,
      numberOfPages,
      onPageChange,
      outOfLabel,
      pageSelectorId,
      pageSelectorLabel,
      ...props
    },
    ref
  ) => {
    const id = useId();
    const classes = cx(className, rootClassName);
    const { options } = usePageOptions(numberOfPages);

    const onSelectionChange = (event: ChangeEvent<HTMLSelectElement>) => {
      const page = event.target.value ? Number(event.target.value) : 1;
      onPageChange(page);
      if (asLink) {
        const url = hrefGetter?.(page) ?? "";
        navigationFunction(page, url);
      }
    };

    return (
      <div className={classes} data-testid={dataTestId} ref={ref} {...props}>
        <div className={cx(`${rootClassName}__select-container`)}>
          <VisuallyHidden>
            <Label data-testid={`${dataTestId}-label`} htmlFor={id}>
              {pageSelectorLabel}
            </Label>
          </VisuallyHidden>
          <Select
            className={cx(`${rootClassName}__select`)}
            data-testid={`${dataTestId}-select`}
            id={pageSelectorId ?? id}
            onChange={onSelectionChange}
            options={options}
            value={String(currentPage)}
          />
        </div>
        <Paragraph
          className={cx(`${rootClassName}__of-total-pages-label`)}
          data-testid={`${dataTestId}-of-total-pages`}
        >
          {`${outOfLabel} ${numberOfPages}`}
        </Paragraph>
      </div>
    );
  }
);

PaginationPageSelector.displayName = "PaginationPageSelector";
